import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as moment from 'moment-timezone';

import { CoverDetails, Offer } from '../_services/types/offers.types';
import { AuthStorageKey } from '../enums/auth-storage-key.enum';
import { Address } from '../ui-kit/components/google-autocomplete/google-autocomplete.component';

export const getComponentByType = (address: Address, type: string) => {
  if (!type) return null;
  if (!address?.address_components || address.address_components.length === 0) return null;
  type = type.toLowerCase();
  const res = address.address_components.find(
    (comp) =>
      comp.types &&
      comp.types.length > 0 &&
      comp.types.findIndex((x) => x.toLowerCase() === type) > -1,
  );
  return res;
};

export const formatPrice = (price: number) => {
  if (price == null || price === undefined) return 'CHF \\';

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return new Intl.NumberFormat('de-CH', options).format(price);
};

export type FormGroupKeys<T extends FormGroup> = keyof T['controls'];

export const getBooleanLabel = (value: boolean): string =>
  value ? $localize`Oui` : $localize`Non`;

/**
 * Normalises a phone number to E.164 ("+41791234567"), or null when it is not a valid number.
 * Brevo rejects a whole contact submission over a malformed SMS attribute, so anything sent
 * there must go through this first.
 */
export const toE164 = (value: string, defaultCountry: CountryCode = 'CH'): string | null => {
  if (!value) return null;
  const parsed = parsePhoneNumberFromString(value, defaultCountry);
  return parsed?.isValid() ? parsed.number : null;
};

export const emailIsValid = (email: string): boolean =>
  !!email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

export const formatCoverDetails = (cover: CoverDetails) => {
  const details = [];
  if (cover.maximumReimbursed) {
    details.push($localize`Jusqu'à ${formatPrice(cover.maximumReimbursed)}`);
  }
  if (cover.deductible !== null && cover.deductible !== undefined) {
    details.push($localize`Franchise de ${formatPrice(cover.deductible)}`);
  }
  if (details) {
    return details.join(' - ');
  }
  return undefined;
};

export const removeDuplicateOffers = (offers: Offer[]) => {
  if (offers)
    for (let i = 0; i < offers.length - 1; i += 1) {
      for (let j = i + 1; j < offers.length; j += 1) {
        if (
          offers[i].insuranceName === offers[j].insuranceName &&
          offers[i].price === offers[j].price &&
          offers[i].covers?.every((x) =>
            offers[j].covers?.some(
              (y) =>
                x.title === y.title &&
                x.description === y.description &&
                x.details === y.details &&
                x.icon === y.icon &&
                x.importance === y.importance &&
                x.price === y.price,
            ),
          ) &&
          offers[j].covers?.every((x) =>
            offers[i].covers?.some(
              (y) =>
                x.title === y.title &&
                x.description === y.description &&
                x.details === y.details &&
                x.icon === y.icon &&
                x.importance === y.importance &&
                x.price === y.price,
            ),
          )
        )
          offers.splice(j, 1);
      }
    }
};

export const getAgeInYears = (value: Date): number =>
  Math.floor((new Date().getTime() - new Date(value).getTime()) / (1000 * 60 * 60 * 24 * 365.25));

export const scrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) element.scrollIntoView();
};

/** Function that takes two strings (full names) and compares them semantically (eg: "first last" and "Last First" will be considered the same) */
export const sameStrings = (name1: string, name2: string): boolean => {
  const name1Array = name1
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .split(' ');

  const name2Array = name2.trim().replace(/\s+/g, ' ').toLowerCase().replace(/\s+/, ' ').split(' ');

  return (
    name1Array.length === name2Array.length &&
    name1Array.every((x) => name2Array.includes(x)) &&
    name2Array.every((x) => name1Array.includes(x))
  );
};

export const dateFromString = (dateString: string): Date => {
  if (!dateString) return null;
  const momentDate = moment(dateString, 'DD/MM/YYYY');
  return momentDate.toDate();
};

export function waitForDomElement(selector: string) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

/**
 * Deep clones the given AbstractControl, preserving values, validators, async validators, and disabled status.
 * @param control AbstractControl
 * @returns AbstractControl
 */
export function cloneAbstractControl<T extends AbstractControl>(control: T): T {
  let newControl: T;

  if (control instanceof FormGroup) {
    const formGroup = new FormGroup({}, control.validator, control.asyncValidator);
    const { controls } = control;

    Object.keys(controls).forEach((key) => {
      formGroup.addControl(key, cloneAbstractControl(controls[key]));
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newControl = formGroup as any;
  } else if (control instanceof FormArray) {
    const formArray = new FormArray([], control.validator, control.asyncValidator);

    control.controls.forEach((formControl) => formArray.push(cloneAbstractControl(formControl)));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newControl = formArray as any;
  } else if (control instanceof FormControl) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newControl = new FormControl(control.value, control.validator, control.asyncValidator) as any;
  } else {
    throw new Error('Error: unexpected control value');
  }

  if (control.disabled) newControl.disable({ emitEvent: false });

  return newControl;
}

export function generateRange(start: number, end: number, reverse = false): number[] {
  const res: number[] = [];
  if (reverse) {
    for (let i = end; i >= start; i -= 1) {
      res.push(i);
    }
  } else {
    for (let i = start; i <= end; i += 1) {
      res.push(i);
    }
  }
  return res;
}

export function clearAuthStorage() {
  localStorage.removeItem(AuthStorageKey.VERIFICATION_MAIL);
  localStorage.removeItem(AuthStorageKey.VERIFICATION_MOBILE);
  localStorage.removeItem(AuthStorageKey.VERIFICATION_EMAIL_DATA);
  localStorage.removeItem(AuthStorageKey.VERIFICATION_MOBILE_DATA);
}

export const isPartnerOfLty = (insuranceName: string): boolean => {
  const lowerCaseName = insuranceName?.toLowerCase();
  return (
    lowerCaseName === 'groupe mutuel' ||
    lowerCaseName === 'swica' ||
    lowerCaseName === 'css' ||
    lowerCaseName === 'helsana'
  );
};

export const getBoolean = (value: string | boolean | number): boolean => {
  switch (value) {
    case true:
    case 'true':
    case 'TRUE':
    case 1:
    case '1':
    case 'on':
    case 'yes':
      return true;
    default:
      return false;
  }
};

export const BOOLEAN_CHOICE_OPTIONS = [
  {
    value: true,
    label: $localize`Oui`,
  },
  {
    value: false,
    label: $localize`Non`,
  },
];

export const isRecordEmpty = (section: Record<string, unknown>): boolean => {
  if (!section) return true;
  return Object.values(section).every(
    (value) =>
      value === null ||
      value === undefined ||
      value === false ||
      (typeof value === 'number' && value === 0),
  );
};

/** A picked address, split the way the address fields store it: the street alone, beside the rest. */
export type SplitAddress = {
  street: string;
  postCode: string;
  city: string;
  state: string;
  /** ISO 3166-1 alpha-2, e.g. "CH". */
  country: string;
};

/**
 * Splits a Google place into the address fields. The street is built from the route and number
 * rather than taken from the name, which is a business's name when a business was picked; the
 * city prefers what the formatted address shows, which the components sometimes lack.
 */
export const splitGoogleAddress = (place: Address): SplitAddress => {
  const component = (type: string) =>
    place.address_components?.find((part) => part.types.includes(type));
  const shownCity = new DOMParser()
    .parseFromString(place.adr_address ?? '', 'text/html')
    .querySelector('.locality')?.textContent;
  const street = [component('route')?.long_name, component('street_number')?.long_name]
    .filter(Boolean)
    .join(' ');
  return {
    street: street || place.name || '',
    postCode: component('postal_code')?.long_name ?? '',
    city:
      shownCity ||
      component('locality')?.long_name ||
      component('administrative_area_level_2')?.long_name ||
      '',
    state: component('administrative_area_level_1')?.long_name ?? '',
    country: component('country')?.short_name ?? '',
  };
};
